import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { CaseStudy } from "./CaseStudy";
import { SectionLabel } from "./MaskedText";

function ProjectRow({ project, index, active, onSelect, onOpen }) {
  const handleClick = () => {
    const touchInput = window.matchMedia?.("(hover: none)").matches;
    if (touchInput && !active) {
      onSelect(index);
      return;
    }
    onOpen(project);
  };

  return (
    <button
      className={`project-row ${active ? "is-active" : ""}`}
      type="button"
      aria-label={`Open case study: ${project.title}`}
      aria-pressed={active}
      onMouseEnter={() => onSelect(index)}
      onFocus={() => onSelect(index)}
      onClick={handleClick}
    >
      <small>{project.number}</small>
      <span className="project-row__title" data-title={project.title}>{project.title}</span>
      <span className="project-row__intent">Case study</span>
    </button>
  );
}
export function ProjectsSection({ projects }) {
  const [activeProject, setActiveProject] = useState(0);
  const [openProject, setOpenProject] = useState(null);
  const previewRef = useRef(null);
  const selected = projects[activeProject];

  useLayoutEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const context = gsap.context(() => {
      const image = previewRef.current?.querySelector("img");
      const copy = previewRef.current?.querySelectorAll("[data-preview-copy]");
      if (!image || reduceMotion) return;

      gsap.fromTo(
        image,
        { clipPath: "inset(100% 0 0 0)", opacity: 0, scale: 1.045, y: 18 },
        { clipPath: "inset(0% 0 0 0)", opacity: 1, scale: 1, y: 0, duration: 0.72, ease: "power4.out" }
      );
      gsap.fromTo(copy, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.42, stagger: 0.05, ease: "power3.out", delay: 0.16 });
    }, previewRef);

    return () => context.revert();
  }, [activeProject]);

  useEffect(() => {
    const preview = previewRef.current;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const pointerFine = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
    if (!preview || reduceMotion || !pointerFine) return undefined;

    const moveX = gsap.quickTo(preview, "x", { duration: 0.7, ease: "power3.out" });
    const moveY = gsap.quickTo(preview, "y", { duration: 0.7, ease: "power3.out" });
    const rotate = gsap.quickTo(preview, "rotation", { duration: 0.7, ease: "power3.out" });

    const handleMove = (event) => {
      const bounds = preview.getBoundingClientRect();
      const x = (event.clientX - bounds.left) / bounds.width - 0.5;
      const y = (event.clientY - bounds.top) / bounds.height - 0.5;
      moveX(x * 10);
      moveY(y * 8);
      rotate(x * 0.6);
    };

    const reset = () => {
      moveX(0);
      moveY(0);
      rotate(0);
    };

    preview.addEventListener("pointermove", handleMove);
    preview.addEventListener("pointerleave", reset);
    return () => {
      preview.removeEventListener("pointermove", handleMove);
      preview.removeEventListener("pointerleave", reset);
      gsap.killTweensOf(preview);
    };
  }, []);

  return (
    <section className="section projects" id="projects">
      <div className="page-width">
        <SectionLabel number="04 /">Selected projects</SectionLabel>
        <div className="projects__layout">
          <div className="projects__list">
            {projects.map((project, index) => (
              <ProjectRow
                key={project.title}
                project={project}
                index={index}
                active={index === activeProject}
                onSelect={setActiveProject}
                onOpen={setOpenProject}
              />
            ))}
          </div>

          <figure className="project-preview" ref={previewRef} aria-live="polite">
            <div className="project-preview__frame">
              <img
                key={selected.image}
                src={selected.image}
                alt={selected.imageAlt}
                width={selected.imageWidth}
                height={selected.imageHeight}
                loading="lazy"
                style={{ objectPosition: selected.imagePosition ?? "center" }}
              />
              <span className="project-preview__index">{selected.number} / {String(projects.length).padStart(2, "0")}</span>
            </div>
            <figcaption>
              <div data-preview-copy>
                <small>{selected.eyebrow}</small>
                <strong>{selected.title}</strong>
              </div>
              <p data-preview-copy>{selected.description}</p>
              <div className="project-preview__stack" data-preview-copy>
                {selected.stack.map((item) => <span key={item}>{item}</span>)}
              </div>
              <button className="project-preview__open" type="button" onClick={() => setOpenProject(selected)} data-preview-copy>
                Open case study
              </button>
            </figcaption>
          </figure>
        </div>
      </div>
      {openProject && <CaseStudy project={openProject} onClose={() => setOpenProject(null)} />}
    </section>
  );
}
